"use client"

import { useRef, useState, useEffect, useLayoutEffect } from "react"
import { usePathname } from "next/navigation"
import { ResponsiveSidebar } from "./responsive-sidebar"
import { Topbar } from "./topbar"
import { BottomNav } from "./bottom-nav"
import { Toaster } from "@/shared/components/ui/sonner"
import { useCharacter } from "@/shared/hooks/use-character"
import { useUIStore } from "@/shared/stores/ui-store"
import type { AuthModalContext } from "@/shared/stores/ui-store"
import { cn } from "@/shared/lib/utils"
import { SIDEBAR_INSET_TRANSITION } from "@/shared/lib/motion"
import { stripLocaleFromPath } from "@/shared/lib/i18n/config"

interface AppShellProps {
  children: React.ReactNode
  /** Optional className for the main content area (e.g. p-0 for full-bleed pages). */
  mainClassName?: string
}

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

/**
 * Single app shell: Sidebar + Topbar + main.
 * Used by (app) and (marketing) layouts so one change updates all pages.
 */
export function AppShell({ children, mainClassName }: AppShellProps) {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed)
  const pathname = usePathname()
  const canonicalPathname = stripLocaleFromPath(pathname)
  const mainRef = useRef<HTMLElement>(null)
  const [topbarHidden, setTopbarHidden] = useState(false)
  const lastScrollY = useRef(0)

  // Individual chat pages: keep topbar visible from md up (tablet/desktop) for nav + credits.
  // Below md, chat uses its own header with a back button for full-bleed mobile layout.
  const isChatConversation = /^\/chat\/.+/.test(canonicalPathname)
  const chatCharacterId = isChatConversation ? canonicalPathname.split("/")[2] ?? "" : ""
  const { data: chatAuthCharacter } = useCharacter(chatCharacterId)
  const authModalContext: AuthModalContext | null = chatAuthCharacter?.avatar
    ? {
        characterId: chatAuthCharacter.id,
        characterName: chatAuthCharacter.name,
        characterAvatar: chatAuthCharacter.avatar,
        source: "chat",
      }
    : null
  const isCreatePage = canonicalPathname === "/create"
  const isGeneratePage = canonicalPathname.startsWith("/generate")
  const isExplorePage = canonicalPathname.startsWith("/explore")
  const isSubscriptionPage = canonicalPathname.startsWith("/subscription")

  useIsomorphicLayoutEffect(() => {
    const isWide = window.matchMedia("(min-width: 1280px)").matches
    setSidebarCollapsed(isChatConversation || !isWide)
  }, [isChatConversation, setSidebarCollapsed])

  useEffect(() => {
    const el = mainRef.current
    if (!el) return

    const onScroll = () => {
      // Mobile: body scrolls (window.scrollY). Desktop: main scrolls (el.scrollTop).
      const y = el.scrollTop || window.scrollY
      if (y > 80 && y - lastScrollY.current > 15) {
        setTopbarHidden(true)
      } else if (lastScrollY.current - y > 10) {
        setTopbarHidden(false)
      }
      lastScrollY.current = y
    }

    el.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      el.removeEventListener("scroll", onScroll)
      window.removeEventListener("scroll", onScroll)
    }
  }, [])

  return (
    <>
      <div
        className={cn(
          "flex min-h-screen [@supports(height:1dvh)]:min-h-dvh bg-background lg:h-screen lg:[@supports(height:1dvh)]:h-dvh lg:overflow-hidden",
          isChatConversation && "h-screen [@supports(height:1dvh)]:h-dvh overflow-hidden"
        )}
      >
        <ResponsiveSidebar authModalContext={authModalContext} />
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col w-full min-w-0",
            SIDEBAR_INSET_TRANSITION,
            sidebarCollapsed ? "lg:ml-[72px]" : "lg:ml-[260px]"
          )}
        >
          <main
            ref={mainRef}
            className={cn(
              "flex-1 min-h-0 overflow-x-clip lg:overflow-x-hidden lg:overflow-y-auto",
              isChatConversation && "flex flex-col overflow-hidden lg:overflow-y-hidden"
            )}
          >
            {/* Topbar: sticky inside scroll container, slides out via transform on mobile.
                On chat conversation: hidden on small mobile (chat header handles exit);
                visible md+ for app nav. Subscription page stays lg+ only. */}
            <div
              className={cn(
                "sticky top-0 z-30 shrink-0 transition-transform duration-300 ease-in-out",
                isChatConversation
                  ? "hidden md:block"
                  : isSubscriptionPage
                    ? "hidden lg:block"
                    : topbarHidden && !isCreatePage && !isExplorePage
                      ? "-translate-y-full lg:translate-y-0"
                      : "translate-y-0"
              )}
            >
              <Topbar authModalContext={authModalContext} />
            </div>
            <div
              className={cn(
                mainClassName ?? "p-4 md:p-6",
                // Create wizard: minimal side padding on mobile to reclaim width.
                isCreatePage && "px-2 py-4 md:p-6",
                isExplorePage && "mx-auto w-full max-w-[1400px]",
                isChatConversation && "flex min-h-0 flex-1 flex-col !p-0"
              )}
            >
              {children}
            </div>
            {/* Spacer for mobile bottom nav so content isn't hidden behind it.
                Not needed on chat conversation pages or create page (bottom nav already hidden). */}
            {!isChatConversation && !isCreatePage && !isGeneratePage && !isExplorePage && !isSubscriptionPage && (
              <div className="h-20 lg:hidden" aria-hidden="true" />
            )}
          </main>
          {!isSubscriptionPage && <BottomNav />}
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  )
}
